"use client"

import { useState } from "react"
import Image from "next/image"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronLeft, ChevronRight, Expand, ImageIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog"

interface ImageGalleryProps {
  images: string[]
}

export function ImageGallery({ images }: ImageGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [fullscreenIndex, setFullscreenIndex] = useState(0)

  const hasImages = images && images.length > 0
  const currentImage = hasImages ? images[currentIndex] : null

  // If there are no images, show a placeholder
  if (!hasImages) {
    return (
      <div className="relative aspect-[16/9] w-full overflow-hidden rounded-xl bg-muted/20 flex items-center justify-center">
        <div className="relative z-10 text-center">
          <ImageIcon className="h-20 w-20 mx-auto text-muted-foreground/30" />
          <p className="mt-4 text-lg text-muted-foreground">No images available</p>
        </div>
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="absolute inset-0 rounded-xl border border-primary/10 neon-glow"></div>
      </div>
    )
  }

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  const openFullscreen = (index: number) => {
    setFullscreenIndex(index)
    setIsOpen(true)
  }

  return (
    <div className="space-y-3">
      {/* Main Image */}
      <div className="relative aspect-[16/9] w-full overflow-hidden rounded-xl bg-gradient-to-br from-background via-background/80 to-background/90">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="relative h-full w-full"
          >
            <Image
              src={currentImage!}
              alt={`Vehicle image ${currentIndex + 1}`}
              fill
              className="object-cover"
              priority={currentIndex === 0}
            />
          </motion.div>
        </AnimatePresence>

        {/* Neon outline */}
        <div className="absolute inset-0 rounded-xl border border-primary/20 neon-glow"></div>

        {/* Gallery Controls */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="absolute inset-0 flex items-center justify-between p-4"
        >
          <Button
            size="icon"
            variant="secondary"
                  onClick={handlePrevious}
            className="h-10 w-10 rounded-full bg-background/60 backdrop-blur-sm hover:bg-background/80 hover-glow"
                >
                  <ChevronLeft className="h-6 w-6" />
            <span className="sr-only">Previous image</span>
          </Button>

          <Button
            size="icon"
            variant="secondary"
                  onClick={handleNext}
            className="h-10 w-10 rounded-full bg-background/60 backdrop-blur-sm hover:bg-background/80 hover-glow"
                >
                  <ChevronRight className="h-6 w-6" />
            <span className="sr-only">Next image</span>
          </Button>
        </motion.div>

        {/* Fullscreen Button */}
        <Button
          size="icon"
          variant="secondary"
          onClick={() => openFullscreen(currentIndex)}
          className="absolute bottom-4 right-4 h-10 w-10 rounded-full bg-background/60 backdrop-blur-sm hover:bg-background/80 hover-glow"
        >
          <Expand className="h-5 w-5" />
          <span className="sr-only">View fullscreen</span>
        </Button>

        {/* Image Counter */}
        <div className="absolute bottom-4 left-4 rounded-full bg-background/50 px-3 py-1 text-xs backdrop-blur-sm neon-border">
          {currentIndex + 1} / {images.length}
          </div>
      </div>

      {/* Thumbnails */}
      <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
        {images.slice(0, 6).map((image, index) => (
                <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`relative aspect-[4/3] overflow-hidden rounded-lg border-2 transition-all ${
              index === currentIndex
                ? "border-primary scale-[1.02] shadow-[0_0_10px_rgba(0,225,225,0.3)]"
                : "border-transparent hover:border-primary/50"
            }`}
                >
                  <Image
              src={image}
                    alt={`Thumbnail ${index + 1}`}
                    fill
                    className="object-cover"
                  />
                </button>
        ))}
        {images.length > 6 && (
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <button className="relative aspect-[4/3] overflow-hidden rounded-lg bg-muted/20 flex items-center justify-center hover:bg-muted/30 transition-colors">
                <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
                <div className="text-center z-10">
                  <span className="text-lg font-medium">+{images.length - 6}</span>
                  <p className="text-xs text-muted-foreground">More</p>
                </div>
              </button>
            </DialogTrigger>
          </Dialog>
        )}
      </div>

      {/* Fullscreen Gallery */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-[95vw] h-[90vh] p-0 bg-transparent border-none">
          <div className="relative w-full h-full bg-background/95 backdrop-blur-xl rounded-xl overflow-hidden border border-primary/20 neon-glow">
            <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
            
            <div className="flex flex-col h-full">
              {/* Main image area */}
              <div className="relative flex-1 min-h-0">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={fullscreenIndex}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 flex items-center justify-center p-4"
                  >
                    <div className="relative w-full h-full max-h-full max-w-full">
                      <Image
                        src={images[fullscreenIndex]}
                        alt={`Vehicle image ${fullscreenIndex + 1}`}
                        fill
                        quality={90}
                        className="object-contain"
                        sizes="(max-width: 1200px) 100vw, 1200px"
                      />
                    </div>
                  </motion.div>
                </AnimatePresence>
                
                {/* Navigation controls */}
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-between px-4 z-20">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setFullscreenIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
                      className="h-12 w-12 rounded-full bg-background/30 backdrop-blur-md hover:bg-background/50 shadow-lg border border-primary/20 hover-glow"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </Button>
                  </motion.div>
                  
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setFullscreenIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
                      className="h-12 w-12 rounded-full bg-background/30 backdrop-blur-md hover:bg-background/50 shadow-lg border border-primary/20 hover-glow"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </Button>
                  </motion.div>
                </div>
                
                {/* Image counter with progress */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center z-20">
                  <div className="bg-background/70 backdrop-blur-md py-1 px-4 rounded-full border border-primary/30 neon-glow shadow-xl">
                    <span className="text-sm font-medium">{fullscreenIndex + 1} / {images.length}</span>
                  </div>
                  <div className="mt-2 w-48 bg-muted/30 rounded-full h-1 overflow-hidden">
                    <motion.div
                      initial={{ width: `${(fullscreenIndex / (images.length - 1)) * 100}%` }}
                      animate={{ width: `${(fullscreenIndex / (images.length - 1)) * 100}%` }}
                      className="h-full bg-primary rounded-full"
                    />
                  </div>
                </div>
              </div>
              
              {/* Thumbnails */}
              <div className="p-4 pt-3 border-t border-muted/20 backdrop-blur-md bg-background/50 z-10">
                <div className="flex gap-2 overflow-x-auto pb-2 px-1 scrollbar-thin scrollbar-thumb-primary/30 scrollbar-track-background/20">
                  {images.map((img, idx) => (
                    <motion.button
                      key={idx}
                      onClick={() => setFullscreenIndex(idx)}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      className={`relative h-20 w-32 shrink-0 overflow-hidden rounded-md transition-all ${
                        idx === fullscreenIndex
                          ? "ring-2 ring-primary ring-offset-2 ring-offset-background/80 shadow-[0_0_15px_rgba(0,225,225,0.4)]"
                          : "opacity-70 hover:opacity-100"
                      }`}
                    >
                      <Image
                        src={img}
                        alt={`Thumbnail ${idx + 1}`}
                        fill
                        className="object-cover"
                        sizes="128px"
                        quality={60}
                      />
                      <div className={`absolute inset-0 border-2 ${
                        idx === fullscreenIndex
                          ? "border-primary/70"
                          : "border-transparent hover:border-primary/30"
                      } transition-colors rounded-md`}></div>
                    </motion.button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}